import React from "react";
import RecognitionGuideLayout from "@/components/RecognitionGuideLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Target, Users, Heart, Settings, DollarSign, Rocket, BarChart3, CheckCircle, ArrowRight, ExternalLink, Plus, Minus } from "lucide-react";

export { Page }

function Page() {
  const steps = [
    {
      number: 1,
      title: "Define Clear Goals and Success Metrics",
      icon: Target,
      description: "Before you launch, align stakeholders on why you're building the program.",
      content: {
        goals: [
          "Do you want to reduce turnover?",
          "Strengthen culture?",
          "Improve productivity?", 
          "Boost engagement survey scores?"
        ],
        action: "Write down these goals and pair them with metrics you can track over time.",
        link: { text: "how to measure the ROI of employee recognition programs", url: "/blog/how-to-measure-the-roi-of-employee-recognition-programs" }
      }
    },
    {
      number: 2,
      title: "Get Leadership and Manager Buy-In",
      icon: Users,
      description: "Recognition programs succeed when leaders model the behavior. If managers aren't participating, employees won't either.",
      content: {
        action: "Share research and case studies internally to secure sponsorship.",
        link: { text: "leadership's role in fostering engagement", url: "/blog/leaderships-role-in-fostering-employee-engagement" }
      }
    },
    {
      number: 3,
      title: "Choose the Right Types of Recognition",
      icon: Heart,
      description: "Blend recognition categories so the program works for everyone.",
      content: {
        types: [
          { name: "Peer-to-Peer", purpose: "for daily appreciation" },
          { name: "Milestones", purpose: "for tenure and career growth" },
          { name: "Values-Based", purpose: "to strengthen culture" },
          { name: "Rewards", purpose: "to make recognition tangible" }
        ],
        links: [
          { text: "15 awesome recognition ideas your staff will love", url: "/blog/15-awesome-employee-recognition-ideas-your-staff-will-love" },
          { text: "50 personalized reward ideas", url: "/blog/50-best-personalized-reward-ideas-for-recognizing-employees" }
        ]
      }
    },
    {
      number: 4,
      title: "Select the Right Platform",
      icon: Settings,
      description: "Technology is the bridge between intent and action.",
      content: {
        criteria: [
          "Integrate with existing workflows (Slack, Microsoft Teams, HRIS)",
          "Offer automation for milestones and birthdays",
          "Support mobile and deskless employees",
          "Provide analytics for continuous improvement"
        ],
        link: { text: "/integrations", url: "/integrations", context: "make recognition seamless in the flow of work" }
      }
    },
    {
      number: 5,
      title: "Establish Budgets and Guardrails",
      icon: DollarSign,
      description: "Financial clarity keeps programs sustainable.",
      content: {
        considerations: [
          "Monthly/quarterly budgets for managers",
          "Guidelines for peer recognition",
          "Approval workflows for larger rewards"
        ],
        links: [
          { text: "employee recognition budget planning", url: "/blog/employee-recognition-budget-planning-the-complete-guide" },
          { text: "crafting an effective budget-friendly program", url: "/blog/crafting-an-effective-budget-friendly-recognition-program" }
        ]
      }
    },
    {
      number: 6,
      title: "Launch with Training and Communication",
      icon: Rocket,
      description: "Don't just flip the switch-explain why the program matters, how it works, and how employees benefit.",
      content: {
        action: "Use kickoff emails, all-hands demos, and manager toolkits.",
        link: { text: "considering an employee recognition program? start here", url: "/blog/considering-an-employee-recognition-program-start-here" }
      }
    },
    {
      number: 7,
      title: "Monitor, Measure, and Improve",
      icon: BarChart3,
      description: "Recognition programs aren't static. Track adoption, recognition frequency, and impact on engagement/retention.",
      content: {
        action: "Use surveys to gather employee feedback, and adjust your program quarterly.",
        links: [
          { text: "how to leverage recognition analytics", url: "/blog/how-to-leverage-employee-recognition-analytics-for-enhanced-engagement" },
          { text: "recognition survey questions", url: "/blog/employee-recognition-survey-questions-to-ask-for-meaningful-insights" }
        ]
      }
    }
  ];

  const assemblyFeatures = [
    "Automate milestones so no big moment is missed",
    "Encourage peer-to-peer appreciation daily",
    "Manage budgets and approvals in one place",
    "Track ROI with built-in analytics"
  ];

  return (
    <RecognitionGuideLayout
      title="Building a Successful Employee Recognition Program"
      description="Learn how to build and implement a successful employee recognition program. Get step-by-step guidance, best practices, and practical tips for creating recognition that drives results."
      currentStep={4}
    >
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium border border-primary/20">
            <Target className="w-4 h-4" />
            Step 4 of 6
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            Building a Successful Employee Recognition Program
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
            A recognition program works best when it's intentional, consistent, and easy to use. Too many companies start with good intentions but end up with low adoption or unclear impact. To avoid that, follow these steps to design a program that fits your culture and delivers measurable business results.
          </p>
        </div>

        {/* Step-by-Step Process */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              7-Step Implementation Process
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A proven framework for building recognition programs that deliver results
            </p>
          </div>

          <div className="grid gap-8">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="border-border/60 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex items-start">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                          <span className="text-lg font-semibold text-primary">{step.number}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {step.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                        
                        <div className="space-y-4">
                          {step.content.goals && (
                            <div>
                              <h4 className="font-medium text-foreground mb-3">Common Goals:</h4>
                              <ul className="space-y-2">
                                {step.content.goals.map((goal, goalIndex) => (
                                  <li key={goalIndex} className="flex items-start gap-2 text-sm">
                                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                                    <span className="text-muted-foreground">{goal}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {step.content.types && (
                            <div className="grid sm:grid-cols-2 gap-4">
                              {step.content.types.map((type, typeIndex) => (
                                <div key={typeIndex} className="flex items-start gap-2 text-sm">
                                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                                  <div>
                                    <span className="font-medium text-foreground">{type.name}</span>
                                    <span className="text-muted-foreground"> {type.purpose}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {step.content.criteria && (
                            <ul className="space-y-2">
                              {step.content.criteria.map((criterion, criterionIndex) => (
                                <li key={criterionIndex} className="flex items-start gap-2 text-sm">
                                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                                  <span className="text-muted-foreground">{criterion}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          
                          {step.content.considerations && (
                            <ul className="space-y-2">
                              {step.content.considerations.map((consideration, considerationIndex) => (
                                <li key={considerationIndex} className="flex items-start gap-2 text-sm">
                                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                                  <span className="text-muted-foreground">{consideration}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          
                          {step.content.action && (
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {step.content.action}
                            </p>
                          )}
                          
                          <div className="flex flex-col sm:flex-row gap-2 pt-2">
                            {step.content.link && (
                              <div className="flex items-center gap-2 text-sm">
                                <ExternalLink className="w-4 h-4 text-primary shrink-0" />
                                <span className="text-muted-foreground">
                                  {step.content.link.context ? `Explore how Assembly's ` : `For inspiration, see `}
                                  <a href={step.content.link.url} className="text-primary hover:text-primary/80 underline">
                                    {step.content.link.text}
                                  </a>
                                  {step.content.link.context && ` ${step.content.link.context}`}.
                                </span>
                              </div>
                            )}
                            
                            {step.content.links && step.content.links.map((link, linkIndex) => (
                              <div key={linkIndex} className="flex items-center gap-2 text-sm">
                                <ExternalLink className="w-4 h-4 text-primary shrink-0" />
                                <a href={link.url} className="text-primary hover:text-primary/80 underline">
                                  {link.text}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Common questions about building employee recognition programs
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {[
              {
                question: "How long does it take to implement an employee recognition program?",
                answer: "Basic recognition programs can launch within 2-4 weeks with the right platform and clear strategy. However, building sustainable recognition habits across your organization typically takes 3-6 months of consistent execution and reinforcement."
              },
              {
                question: "What's the most important step in building a recognition program?",
                answer: "Getting leadership buy-in is critical. Recognition programs succeed when leaders model the behavior and managers actively participate. Without leadership support, employee adoption rates remain low."
              },
              {
                question: "How much should companies budget for employee recognition programs?",
                answer: "Most successful programs budget 1-2% of payroll for recognition, including platform costs and rewards. However, many high-impact recognition activities require minimal budget - the key is consistency and authenticity."
              },
              {
                question: "What features should I look for in a recognition platform?",
                answer: "Essential features include integration with existing workflows (Slack, Teams), automation for milestones, mobile accessibility, analytics capabilities, and flexible reward options that suit diverse employee preferences."
              }
            ].map((faq, index) => (
              <Collapsible key={index}>
                <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
                  <CollapsibleTrigger className="w-full group">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-left flex items-center justify-between">
                        <span className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                        <div className="flex items-center">
                          <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=open]:hidden" />
                          <Minus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=closed]:hidden" />
                        </div>
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </section>

        {/* Bringing It All Together */}
        <section>
          <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
            <CardContent className="p-8 text-center space-y-6">
              <div className="space-y-3">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                
                <h3 className="text-2xl font-bold text-foreground">
                  Bringing It All Together
                </h3>
                
                <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  The most successful recognition programs are simple to use, aligned to culture, and continuously improved.
                </p>
              </div>
              
              <div className="text-left max-w-3xl mx-auto">
                <p className="text-muted-foreground mb-4">With Assembly, you can:</p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {assemblyFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-4 pt-4">
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Recognition should never feel like another HR task-it should feel like a natural part of work. Before choosing your solution, explore our research-backed <a href="https://joinassembly.com/best/employee-recognition-software/top" className="text-primary hover:text-primary/80 underline">Employee Recognition Software rankings</a> to understand which platforms deliver the best results for organizations like yours.
                </p>
                
                <div className="flex justify-center">
                  <a href="/book-demo" className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors">
                    Get Started with Assembly
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </RecognitionGuideLayout>
  );
}